import { CartOrderForm } from '@b2b/types/cart';
import { CartEventOrderForm, isCartEventOrderForm } from '@b2b/types/event';
import { CartMembershipOrderForm, isMembershipOrderForm } from '@b2b/types/membership';
import { Membership } from '@b2b/types/membership';
import { SalesChannelType } from '@b2b/types/core';
import { OrderFormWithResponse, isOrderFormWithResponse } from '../types';

/**
 * Filter event forms by sales channel preference
 * Uses the 'preferences' object on event forms
 */
export function filterEventFormsBySalesChannel(
  forms: CartEventOrderForm[],
  salesChannel: string
): CartEventOrderForm[] {
  return forms.filter((form) => {
    // Check if the form has preferences for the specified sales channel
    if (form.preferences?.[salesChannel]) {
      const preference = form.preferences[salesChannel];
      return preference === 'displayed' || preference === 'required';
    }
    return false;
  });
}

/**
 * Normalizes a sales channel string for comparison by stripping underscores and lowercasing.
 * Handles all formats the API may return: camelCase ("insideSalesIndividual"),
 * PascalCase ("InsideSalesIndividual"), and UPPER_SNAKE_CASE ("INSIDE_SALES_INDIVIDUAL").
 */
function normalizeChannel(s: string): string {
  return s.replace(/_/g, '').toLowerCase();
}

function isSameChannel(a: string, b: string): boolean {
  return normalizeChannel(a) === normalizeChannel(b);
}

/**
 * Filter membership forms by sales channel and membership purchase type
 * 
 * Logic:
 * 1. Check if form applies to the membership purchase type (gift vs standard)
 * 2. Check if form is enabled for the sales channel (displayForm must be true)
 * 3. Return filtered forms
 * 
 * @param forms - Array of membership forms
 * @param memberships - Array of membership items in cart
 * @param salesChannel - The sales channel to filter by (e.g., 'insideSalesIndividual')
 */
export function filterMembershipFormsBySalesChannel(
  forms: CartMembershipOrderForm[],
  memberships: Membership[],
  salesChannel: string
): CartMembershipOrderForm[] {
  return forms.filter((form) => {
    // Step 1: Check if form applies to ANY membership in cart based on purchase type
    const appliesToAnyMembership = memberships.some((membership) => {
      const isGift = membership.membershipInfo?.isGift ?? false;
      
      if (isGift) {
        // For gift memberships: form must have displayFormForGiftPurchase = true
        return form.displayFormForGiftPurchase === true;
      } else {
        // For standard (non-gift) memberships: form must have displayFormForStandardPurchase = true
        return form.displayFormForStandardPurchase === true;
      }
    });

    if (!appliesToAnyMembership) {
      return false; // Form doesn't apply to any membership in cart
    }

    // Step 2: Check sales channel restriction
    const channelRestriction = form.formPreferences?.find(
      (pref) => isSameChannel(pref.saleChannel, salesChannel)
    );

    if (!channelRestriction) {
      return false; // No restriction found for this channel
    }

    if (!channelRestriction.displayForm) {
      return false; // Form is explicitly disabled for this channel
    }

    // ✅ Form passed all checks
    return true;
  });
}

export function isOrderFormRequired(
  form: CartEventOrderForm,
  salesChannel: string
): boolean {
  return form.preferences?.[salesChannel] === 'required';
}

/**
 * Determine if a form is required based on sales channel
 */
export function isFormRequired(
  form: CartOrderForm,
  salesChannel: string
): boolean {
  // Check event forms (use preferences)
  if ('preferences' in form && form.preferences?.[salesChannel]) {
    return form.preferences[salesChannel] === 'required';
  }

  // Check membership forms (use formPreferences)
  if ('formPreferences' in form && form.formPreferences) {
    const preference = form.formPreferences.find((p) => isSameChannel(p.saleChannel, salesChannel));
    return preference?.formResponseRequired ?? false;
  }

  return false;
}

/**
 * Returns the display name for a form's associated item (event name or membership name).
 */
function getItemName(form: CartOrderForm): string {
  if (isCartEventOrderForm(form) && form.event) {
    const datePart = form.event.displayStartDate
      ? ` on ${form.event.displayStartDate} at ${form.event.displayStartTime}`
      : '';
    return `${form.event.name}${datePart}`;
  }
  if (isMembershipOrderForm(form)) {
    return form.membershipName;
  }
  return '';
}

/**
 * Deduplicate forms based on formDisplaySettings.
 * Each returned form includes a `relatedItemNames` array:
 * - `perCart` forms: all item names sharing that form (>1 means "Multiple")
 * - `perItem` / other forms: the single item name for that row
 *
 * @param forms - Array of cart order forms to deduplicate
 * @returns Deduplicated array of forms with relatedItemNames
 */
export function deduplicateFormsByDisplaySettings(
  forms: CartOrderForm[]
): (CartOrderForm & { relatedItemNames: string[] })[] {
  // First pass: collect all unique item names for each perCart form ID
  const itemNamesByFormId = new Map<string, string[]>();
  forms.forEach((form) => {
    if (form.formDisplaySettings === 'perCart') {
      const name = getItemName(form);
      let names = itemNamesByFormId.get(form.id);
      if (!names) {
        names = [];
        itemNamesByFormId.set(form.id, names);
      }
      if (name && !names.includes(name)) {
        names.push(name);
      }
    }
  });

  const seen = new Map<string, CartOrderForm & { relatedItemNames: string[] }>();

  forms.forEach((form) => {
    if (form.formDisplaySettings === 'perCart') {
      // Only keep first occurrence; attach all related item names
      if (!seen.has(form.id)) {
        const relatedItemNames = itemNamesByFormId.get(form.id) ?? [getItemName(form)];
        seen.set(form.id, { ...form, relatedItemNames });
      } else {
        // Already added this form ID; if this occurrence is required, propagate that to the existing entry
        const existing = seen.get(form.id);
        const channel = isCartEventOrderForm(form) ? 'insideSales' : 'insideSalesIndividual';
        const isRequired = isFormRequired(form, channel);

        if (existing && isRequired) {
          if (isCartEventOrderForm(form) && isCartEventOrderForm(existing)) {
            seen.set(form.id, {
              ...existing,
              preferences: { ...existing.preferences, insideSales: form.preferences?.insideSales },
            });
          } else if (isMembershipOrderForm(existing)) {
            seen.set(form.id, {
              ...existing,
              formPreferences: (existing.formPreferences ?? []).map((pref) =>
                isSameChannel(pref.saleChannel, 'insideSalesIndividual')
                  ? { ...pref, formResponseRequired: true }
                  : pref
              ),
            });
          }
        }
      }
    } else if (form.formDisplaySettings === 'perItem') {
      // Keep all occurrences with their own item name
      const key = `${form.id}-${seen.size}`;
      seen.set(key, { ...form, relatedItemNames: [getItemName(form)] });
    } else {
      seen.set(form.id, { ...form, relatedItemNames: [getItemName(form)] });
    }
  });

  return Array.from(seen.values());
}


export function deduplicateOrderFormsByDisplaySettings(
  forms: OrderFormWithResponse[]
): (OrderFormWithResponse & { relatedItemNames: string[] })[] {
  // First pass: collect all unique item names for each perCart form ID
  const itemNamesByFormId = new Map<string, string[]>();
  forms.forEach((form) => {
    if (form.formDisplaySettings === 'perCart') {
      const name = getItemName(form);
      let names = itemNamesByFormId.get(form.id);
      if (!names) {
        names = [];
        itemNamesByFormId.set(form.id, names);
      }
      if (name && !names.includes(name)) {
        names.push(name);
      }
    }
  });

  const seen = new Map<string, OrderFormWithResponse & { relatedItemNames: string[] }>();

  forms.forEach((form) => {
    if (form.formDisplaySettings === 'perCart') {
      // Only keep first occurrence; attach all related item names
      if (!seen.has(form.id)) {
        const relatedItemNames = itemNamesByFormId.get(form.id) ?? [getItemName(form)];
        seen.set(form.id, { ...form, relatedItemNames });
      } else {
        // Already added this form ID; if this occurrence is required, propagate that to the existing entry
        const existing = seen.get(form.id);
        const isRequired = isFormRequired(form, 'insideSales');

        if (existing && isRequired) {
          if (isOrderFormWithResponse(form) && isOrderFormWithResponse(existing)) {
            seen.set(form.id, {
              ...existing,
              preferences: { ...existing.preferences, insideSales: form.preferences?.insideSales },
              response: existing.response ?? form.response,
            });
          }
          // Membership forms shouldn't be duplicated in the first place since they use perItem display settings, 
          // but if we did have duplicates we would want to propagate the required setting as well.
          // However, since OrderFormWithResponse is only for event forms, we don't need to handle that case here.
        }
      }
    } else if (form.formDisplaySettings === 'perItem') {
      // Keep all occurrences with their own item name
      const key = `${form.id}-${seen.size}`;
      seen.set(key, { ...form, relatedItemNames: [getItemName(form)] });
    } else {
      seen.set(form.id, { ...form, relatedItemNames: [getItemName(form)] });
    }
  });

  return Array.from(seen.values());
}